<template>
  <div class="applications-container">
    <div class="back-navigation" style="margin-bottom: 20px;">
      <el-button
        type="text"
        icon="el-icon-arrow-left"
        @click="goBackToMembers"
      >
        Back to Club Members
      </el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="applications"
      style="width: 100%"
    >
      <el-table-column label="Student" min-width="150">
        <template slot-scope="scope">
          <div class="student-info">
            {{ scope.row.student.student_lastName_pinyin && scope.row.student.student_givenName_pinyin ? 
               scope.row.student.student_givenName_pinyin + ' ' + scope.row.student.student_lastName_pinyin :
               scope.row.student.student_givenName + ' ' + scope.row.student.student_lastName }}
            <div class="student-email">{{ scope.row.student.student_email }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Personal Statement" prop="personal_statement" show-overflow-tooltip min-width="300">
        <template slot-scope="scope">
          <el-popover
            placement="top-start"
            width="800"
            trigger="hover"
          >
            <div style="max-height: 200px; overflow-y: auto;">{{ scope.row.personal_statement }}</div>
            <div slot="reference" class="statement-preview">
              {{ scope.row.personal_statement }}
            </div>
          </el-popover>
          <el-button 
            type="text" 
            size="small" 
            @click="showFullStatement(scope.row)"
          >
            View Full
          </el-button>
        </template>
      </el-table-column>

      <el-table-column label="Status" width="120">
        <template slot-scope="scope">
          <el-tag 
            :type="getStatusType(scope.row.status)"
            size="small"
          >
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Applied Date" width="150">
        <template slot-scope="scope">
          {{ formatDate(scope.row.create_time) }}
        </template>
      </el-table-column>

      <el-table-column label="Actions" width="200" align="right">
        <template slot-scope="scope">
          <el-button 
            v-if="scope.row.status === 'pending'"
            type="success" 
            size="small"
            @click="confirmAction(scope.row, 'approved')"
          >
            Approve
          </el-button>
          <el-button 
            v-if="scope.row.status === 'pending'"
            type="danger" 
            size="small"
            @click="confirmAction(scope.row, 'rejected')"
          >
            Reject
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      title="Personal Statement"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <div class="full-statement">{{ selectedStatement }}</div>
    </el-dialog>

    <div v-if="!applications.length" class="empty-state">
      <i class="el-icon-document"></i>
      <p>No applications yet</p>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId } from '@/utils/store'
export default {
  name: 'Applications',
  props: {
    club_id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      applications: [],
      loading: false,
      dialogVisible: false,
      selectedStatement: ''
    }
  },
  created() {
    this.fetchApplications()
  },
  methods: {
    async fetchApplications() {
      this.loading = true
      try {
        const response = await postRequest('getClubApplications', {
          club_id: this.club_id
        })
        if (response.data.code === 0) {
          this.applications = response.data.data
        }
      } catch (error) {
        console.error('Error fetching applications:', error)
        this.$message.error('Failed to load applications')
      } finally {
        this.loading = false
      }
    },
    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleDateString()
    },
    getStatusType(status) {
      const types = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger'
      }
      return types[status] || 'info'
    },
    async handleApplication(application, newStatus) {
      try {
        const response = await postRequest('changeClubApplicationStatus', {
          application_id: application._id.$id,
          status: newStatus,
          advisor_id: getUserId()
        })
        
        if (response.data.code === 0) {
          this.$message.success(`Application ${newStatus} successfully`)
          this.fetchApplications()
        }
      } catch (error) {
        console.error('Error updating application:', error)
        this.$message.error('Failed to update application')
      }
    },
    showFullStatement(application) {
      this.selectedStatement = application.personal_statement
      this.dialogVisible = true
    },
    confirmAction(application, newStatus) {
      const action = newStatus === 'approved' ? 'approve' : 'reject'
      const studentName = application.student.student_givenName_pinyin && application.student.student_lastName_pinyin ?
        `${application.student.student_givenName_pinyin} ${application.student.student_lastName_pinyin}` :
        `${application.student.student_givenName} ${application.student.student_lastName}`

      this.$confirm(
        `Are you sure you want to ${action} the application from ${studentName}?`,
        'Confirm Action',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel',
          type: newStatus === 'approved' ? 'success' : 'warning'
        }
      ).then(() => {
        this.handleApplication(application, newStatus)
      }).catch(() => {
        this.$message({
          type: 'info',
          message: 'Action canceled'
        })
      })
    },
    goBackToMembers() {
      this.$emit('change-tab', 'members')
    }
  }
}
</script>

<style lang="scss" scoped>
.applications-container {
  padding: 20px 0;

  .back-navigation {
    .el-button--text {
      font-size: 14px;
      padding: 8px 12px;

      &:hover {
        color: #409EFF;
        background-color: #ecf5ff;
      }
    }
  }

  .student-info {
    .student-email {
      font-size: 12px;
      color: #909399;
      margin-top: 4px;
    }
  }

  .statement-preview {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
  }

  .empty-state {
    text-align: center;
    padding: 40px 0;
    color: #909399;

    i {
      font-size: 48px;
      margin-bottom: 16px;
    }

    p {
      margin: 0;
      font-size: 16px;
    }
  }

  .full-statement {
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style> 